import * as React from 'react';
import { Box, Button, ThemeProvider, Typography } from '@mui/material';
import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { generateTheme } from '../themes';
import { detectLang, DEFAULT_LANG } from '../utils/language';

const CheckIcon = ({ size = 20, color = '#0091A5' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ThankYouSlice = ({ data }) => {
    const {
        colorTheme = 'off-white',
        eyebrow = '',
        title = '',
        intro = '',
        whatNextHeading = '',
        steps = [],
        directHeading = '',
        directEmail = '',
        backHomeLabel = '',
        backHomeUrl = ''
    } = data || {};

    const lang = typeof window !== 'undefined' ? detectLang(window.location.pathname) : DEFAULT_LANG;
    const fallbackHome = lang === DEFAULT_LANG ? '/' : `/${lang}/`;
    const homeHref = backHomeUrl || fallbackHome;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const targets = gsap.utils.toArray('.ty-animate', containerRef.current);
        targets.forEach((el, i) => {
            gsap.fromTo(el,
                { opacity: 0, y: 12 },
                { opacity: 1, y: 0, duration: 0.6, delay: 0.06 * i, ease: 'easeIn' }
            );
        });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (th) => th.palette.background.default,
                    minHeight: { xs: '70vh', md: '80vh' },
                    px: { xs: 3, md: 7.5 },
                    pt: { xs: 6, md: 10 },
                    pb: { xs: 8, md: 12 }
                }}
            >
                <Box sx={{ maxWidth: 760, mx: 'auto' }}>
                    {/* Top header — mirrors ContactSlice header */}
                    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                        {eyebrow && (
                            <Typography
                                className="ty-animate"
                                component="span"
                                sx={{
                                    display: 'block',
                                    color: 'text.secondary',
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    opacity: 0.7,
                                    mb: 1
                                }}
                                dangerouslySetInnerHTML={{ __html: eyebrow }}
                            />
                        )}
                        {eyebrow && (
                            <Box
                                aria-hidden="true"
                                className="ty-animate"
                                sx={{
                                    height: '4px',
                                    width: 160,
                                    borderRadius: '2px',
                                    mx: 'auto',
                                    mb: { xs: 2.5, md: 3 },
                                    background: 'linear-gradient(90deg, rgba(92, 51, 215, 0) 0%, rgb(92, 51, 215) 100%)'
                                }}
                            />
                        )}
                        {title && (
                            <Typography
                                className="ty-animate"
                                component="h1"
                                sx={{
                                    fontFamily: 'Kanit, sans-serif',
                                    fontWeight: 900,
                                    fontSize: { xs: '40px', md: '64px' },
                                    lineHeight: 0.95,
                                    letterSpacing: '-1.5px',
                                    textTransform: 'uppercase',
                                    color: 'text.primary',
                                    m: 0,
                                    mb: { xs: 2, md: 3 }
                                }}
                                dangerouslySetInnerHTML={{ __html: title }}
                            />
                        )}
                        {intro && (
                            <Typography
                                className="ty-animate"
                                component="p"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.0625rem' },
                                    color: 'text.primary',
                                    lineHeight: 1.6,
                                    maxWidth: 560,
                                    mx: 'auto',
                                    m: 0
                                }}
                                dangerouslySetInnerHTML={{ __html: intro }}
                            />
                        )}
                    </Box>

                    {/* "What happens next" card */}
                    {(whatNextHeading || steps.length > 0) && (
                        <Box
                            className="ty-animate"
                            sx={{
                                background: '#fff',
                                borderRadius: { xs: '20px', md: '24px' },
                                p: { xs: '24px', md: '36px' },
                                border: '0.5px solid #E8E4DF',
                                boxShadow: '0 4px 24px rgba(22, 18, 28, 0.06)',
                                mb: { xs: 4, md: 5 }
                            }}
                        >
                            {whatNextHeading && (
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontFamily: 'Kanit, sans-serif',
                                        fontWeight: 800,
                                        fontSize: { xs: '1.25rem', md: '1.5rem' },
                                        color: 'text.primary',
                                        lineHeight: 1.15,
                                        letterSpacing: '-0.5px',
                                        m: 0,
                                        mb: 2.5
                                    }}
                                    dangerouslySetInnerHTML={{ __html: whatNextHeading }}
                                />
                            )}
                            {steps.length > 0 && (
                                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                                    {steps.map((s, i) => (
                                        <Box
                                            key={i}
                                            component="li"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 1.5,
                                                py: 1.25
                                            }}
                                        >
                                            <Box sx={{ flexShrink: 0, mt: '2px' }}>
                                                <CheckIcon size={20} />
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                {s.title && (
                                                    <Typography
                                                        component="h3"
                                                        sx={{
                                                            fontSize: '0.9375rem',
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            lineHeight: 1.3,
                                                            m: 0,
                                                            mb: s.description ? 0.25 : 0
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: s.title }}
                                                    />
                                                )}
                                                {s.description && (
                                                    <Typography
                                                        component="p"
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            color: 'text.secondary',
                                                            lineHeight: 1.5,
                                                            m: 0
                                                        }}
                                                        dangerouslySetInnerHTML={{ __html: s.description }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Direct contact + back button */}
                    <Box className="ty-animate" sx={{ textAlign: 'center' }}>
                        {directHeading && (
                            <Typography
                                component="p"
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.18em',
                                    textTransform: 'uppercase',
                                    color: 'text.secondary',
                                    opacity: 0.7,
                                    m: 0,
                                    mb: 1
                                }}
                                dangerouslySetInnerHTML={{ __html: directHeading }}
                            />
                        )}
                        {directEmail && (
                            <Typography
                                component="a"
                                href={`mailto:${directEmail}`}
                                sx={{
                                    display: 'inline-block',
                                    fontSize: '0.9375rem',
                                    color: '#5C33D7',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    mb: 4,
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                {directEmail}
                            </Typography>
                        )}
                        {backHomeLabel && (
                            <Box>
                                <Button
                                    href={homeHref}
                                    disableElevation
                                    sx={{
                                        py: 1.5,
                                        px: 4,
                                        backgroundColor: '#5C33D7',
                                        color: '#fff',
                                        borderRadius: '10px',
                                        fontSize: '0.9375rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontFamily: 'Roboto, sans-serif',
                                        '&:hover': { backgroundColor: '#4C1D95' }
                                    }}
                                >
                                    {backHomeLabel}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default ThankYouSlice;
